import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Star, BadgeCheck } from 'lucide-react';
import { ReviewComment } from '@/components/ReviewComment';
import { formatDateOnly } from '@/lib/dateFR';
import { isTravellerReview } from '@/lib/publicReviews';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Travellers' reviews of a listing (airport transfers, private drivers):
 * average out of 5, number of verified reviews (each one comes from a
 * completed Ridalo booking), comments and dates. Real published reviews only
 * (GET /reviews/service/{id} and /reviews/summary/{id}); a discreet empty
 * state when there are none — never a placeholder rating.
 */
export const ServiceReviewsSection = ({ serviceId }) => {
  const { t, i18n } = useTranslation();
  const lang = String(i18n.language || 'fr').toLowerCase().startsWith('en') ? 'en' : 'fr';
  const [reviews, setReviews] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!serviceId) return undefined;
    let alive = true;
    axios.get(`${API}/reviews/service/${serviceId}`, { params: { lang } })
      .then((r) => { if (alive) setReviews((r.data || []).filter(isTravellerReview)); })
      .catch(() => { if (alive) setReviews([]); });
    axios.get(`${API}/reviews/summary/${serviceId}`)
      .then((r) => { if (alive) setSummary(r.data); })
      .catch(() => { if (alive) setSummary(null); });
    return () => { alive = false; };
  }, [serviceId, lang]);

  if (reviews === null) return null;
  const count = reviews.length;
  const avg = count && summary?.avg ? summary.avg : null;

  return (
    <section className="card-dashboard" data-testid="service-reviews">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h2 className="text-xl font-medium text-primary">{t('scooters.detail.sections.reviews')}</h2>
        {avg && (
          <div className="flex items-center gap-1" data-testid="service-reviews-summary">
            <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
            <span className="font-bold text-primary" data-testid="service-reviews-avg">{Number(avg).toFixed(1)}</span>
            <span className="text-sm text-ink-light">/ 5 · {t('serviceDetail.reviews.verifiedCount', { count })}</span>
          </div>
        )}
      </div>
      {count === 0 ? (
        <p className="text-sm text-ink-muted" data-testid="service-reviews-empty">{t('serviceDetail.reviews.empty')}</p>
      ) : (
        <div className="space-y-4" data-testid="service-reviews-list">
          {reviews.map((r) => (
            <div key={r.review_id} className="border-b border-slate-100 last:border-0 pb-4 last:pb-0" data-testid={`service-review-${r.review_id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-primary flex items-center gap-1">
                    <span className="truncate">{r.reviewer_name}</span>
                    <BadgeCheck className="w-3.5 h-3.5 text-accent shrink-0" aria-label={t('scooters.detail.verifiedClient')} />
                  </div>
                  <div className="text-xs text-ink-muted">{r.created_at ? formatDateOnly(r.created_at) : ''}</div>
                </div>
                <div className="flex items-center gap-0.5 shrink-0" aria-label={`${r.rating}/5`}>
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-200'}`} />
                  ))}
                </div>
              </div>
              <ReviewComment comment={r.comment} translations={r.comment_translations} sourceLang={r.comment_source_lang} />
            </div>
          ))}
          <p className="text-xs text-ink-muted">{t('serviceDetail.reviews.verifiedNote')}</p>
        </div>
      )}
    </section>
  );
};

export default ServiceReviewsSection;
